import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

export interface CertificateStatus {
  installed: boolean;
  /** Present only when `installed` is false: why the stored certificate is not in force. */
  problem?: string;
  /**
   * Present only when `installed` is false. True for a certificate that lapsed, which has stopped
   * every device; false when none was ever installed.
   */
  stored?: boolean;
  certSerial?: number;
  customerId?: string;
  customerName?: string;
  /** The certified server name. Absent on certificates issued before the field existed. */
  serverName?: string;
  /** Hostnames the certificate is valid at. Checked by the apps, not by the server. */
  permittedHosts?: string[];
  notBefore?: string;
  notAfter?: string;
  /** Whole days until `notAfter` on the server's clock. */
  expiresInDays?: number;
  /** 60, 30, 14 or 7 from 60 days before `notAfter`: the reminder point most recently crossed. */
  expiryReminderDays?: number;
}

export interface InstallationStatus {
  installationId: string;
  fingerprint: string;
  /** The host changed under an activated installation; licenses are suspended. */
  fingerprintMismatch: boolean;
  activated: boolean;
  licenseFileCount: number;
  licenseCount: number;
  /** Licenses missing an activation id, a password, or both. */
  licensesAwaitingCredentials: number;
  certificate: CertificateStatus;
  /** What this server asks to be called. Absent until it has been set. */
  requestedName?: string;
  /** Hosts it asks to be reached at. Empty until they have been set. */
  requestedHosts: string[];
  createdAt: string;
  lastSyncAt?: string;
  serverVersion: string;
}

export const installationApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getInstallation: builder.query<InstallationStatus, void>({
      query: () => '/v1/installation',
      providesTags: ['Installation'],
      onQueryStarted: handleApiResult(),
    }),
    /**
     * The two claims this installation makes about itself. Saving them changes nothing on its
     * own: an installed certificate keeps working, and the new values reach a device only after
     * another enrolment round trip.
     */
    updateEnrolmentDetails: builder.mutation<void, { serverName: string; hosts: string[] }>({
      query: (details) => ({ url: '/v1/installation/enrolment', method: 'PUT', body: details }),
      invalidatesTags: ['Installation'],
    }),
    // Returned as text so the panel can hand it over as a downloadable file.
    getEnrolmentRequest: builder.query<string, void>({
      query: () => ({
        url: '/v1/installation/enrolment-request',
        responseHandler: (response: Response) => response.text(),
      }),
      onQueryStarted: handleApiResult(),
    }),
    installCertificate: builder.mutation<CertificateStatus, File>({
      query: (file) => {
        const body = new FormData();
        body.append('file', file);

        // No Content-Type header: the browser must set the multipart boundary itself.
        return { url: '/v1/installation/certificate', method: 'POST', body };
      },
      invalidatesTags: ['Installation'],
    }),
  }),
});

export const {
  useGetInstallationQuery,
  useUpdateEnrolmentDetailsMutation,
  useLazyGetEnrolmentRequestQuery,
  useInstallCertificateMutation,
} = installationApiSlice;
